import { GameState } from '../core/GameState';
import { bn, Decimal } from '../core/BigNumber';
import { eventBus } from '../core/EventBus';
import { UPGRADES, UpgradeDef } from '../data/upgrades';

export class UpgradeSystem {
  init(state: GameState): void {
    for (const def of UPGRADES) {
      if (!state.upgrades.has(def.id)) {
        state.upgrades.set(def.id, { unlocked: false, bought: false });
      }
    }
  }

  buy(state: GameState, upgradeId: string): boolean {
    const def = this.getDef(upgradeId);
    if (!def) return false;
    const us = state.upgrades.get(upgradeId);
    if (!us || !us.unlocked || us.bought) return false;
    const price = bn(def.price);
    if (state.cookies.lt(price)) return false;

    state.cookies = state.cookies.sub(price);
    us.bought = true;

    eventBus.emit('upgrade:bought', upgradeId);
    eventBus.emit('gains:recalculate');
    return true;
  }

  checkUnlocks(state: GameState): void {
    for (const def of UPGRADES) {
      const us = state.upgrades.get(def.id);
      if (!us || us.unlocked || us.bought) continue;

      if (this.shouldUnlock(state, def)) {
        us.unlocked = true;
        eventBus.emit('upgrade:unlocked', def.id);
      }
    }
  }

  getAvailable(state: GameState): UpgradeDef[] {
    return UPGRADES.filter((def) => {
      const us = state.upgrades.get(def.id);
      return us && us.unlocked && !us.bought;
    }).sort((a, b) => a.price - b.price);
  }

  getOwned(state: GameState): UpgradeDef[] {
    return UPGRADES.filter((def) => {
      const us = state.upgrades.get(def.id);
      return us && us.bought;
    });
  }

  getCookieMultiplier(state: GameState): Decimal {
    let mult = bn(1);
    for (const def of UPGRADES) {
      if (def.pool !== 'cookie' || !def.power) continue;
      const us = state.upgrades.get(def.id);
      if (!us?.bought) continue;
      if (!def.buildingId) {
        mult = mult.mul(1 + def.power / 100);
      }
    }
    return mult;
  }

  getBuildingMultiplier(state: GameState, buildingId: string): Decimal {
    let mult = bn(1);
    for (const def of UPGRADES) {
      if (def.buildingId !== buildingId) continue;
      const us = state.upgrades.get(def.id);
      if (!us?.bought) continue;
      mult = mult.mul(2);
    }
    return mult;
  }

  getDef(id: string): UpgradeDef | undefined {
    return UPGRADES.find((u) => u.id === id);
  }

  private shouldUnlock(state: GameState, def: UpgradeDef): boolean {
    if (def.unlockCondition) {
      return this.checkCondition(state, def.unlockCondition);
    }

    if (def.buildingId) {
      const bs = state.buildings.get(def.buildingId);
      if (!bs) return false;
      const thresholds = [1, 1, 5, 25, 50, 100, 150, 200, 250, 300, 350, 400, 450, 500];
      const needed = thresholds[Math.min(def.tier, thresholds.length - 1)] ?? 1;
      return bs.amount >= needed;
    }

    return state.cookiesEarned.gte(def.price / 5);
  }

  private checkCondition(state: GameState, condition: string): boolean {
    const [type, valueStr] = condition.split(':');
    const value = parseInt(valueStr, 10);
    if (type === 'clicks') return state.cookieClicks >= value;
    if (type === 'cookies') return state.cookiesEarned.gte(value);
    return false;
  }
}
